/**
 * GULAR-lite — the acid/psy bass voice.
 *
 * Transferred from Chirp/bittern.html, the GULAR module. Kept: unison saw fan
 * with a sub, two cascaded lowpass poles (the squelch), the amp ADSR and the
 * filter envelope, the tanh drive stage, and the glide handle that makes a
 * slide a tie rather than a retrigger. Dropped: chorus, PWM, the LFO dock
 * matrix, the custom PeriodicWave drawbars, MIDI. Those are Chirp's bench
 * features; this instrument has one screen and a pair of hands.
 *
 *   unison saws (+ sub) -> lowpass f1 -> lowpass f2 -> ADSR -> drive -> out
 *
 * The engine takes its output node from the caller and never touches
 * ctx.destination.
 */

import { clamp, driveCurve, midiHz, unisonFan } from '../common/pitch';
import type { BassParams } from './patches';

export interface SlideHandle {
  /** Re-pitch THIS voice — a tie, not a retrigger. The whole 303 grammar. */
  glideTo(midi: number, at: number, time: number): void;
}

export interface BassEngine {
  noteOn(midi: number, when: number, dur: number, vel?: number): SlideHandle;
  /** Continuous params always land through setTargetAtTime — no clicks. */
  setCutoff(hz: number): void;
  setRes(q: number): void;
  setDrive(v: number): void;
  setLevel(v: number): void;
  readonly params: BassParams;
  dispose(): void;
}

export function createBassEngine(ctx: BaseAudioContext, out: AudioNode, params: BassParams, level = 0.8): BassEngine {
  const P: BassParams = { ...params };

  const master = ctx.createGain();
  master.gain.value = level * level; // perceptual taper
  const pre = ctx.createGain();
  // TWO shapers, crossfaded. Swapping a WaveShaper's curve while signal flows
  // is an instantaneous transfer-function change — a zipper on every Drive
  // slider move. The new curve lands on the silent shaper and an equal-power
  // fade brings it in over ~30 ms.
  const shaperA = ctx.createWaveShaper();
  const shaperB = ctx.createWaveShaper();
  shaperA.oversample = shaperB.oversample = '2x';
  const fadeA = ctx.createGain();
  const fadeB = ctx.createGain();
  fadeA.gain.value = 1; fadeB.gain.value = 0;
  const post = ctx.createGain();
  const bus = ctx.createGain();

  bus.connect(pre);
  pre.connect(shaperA); shaperA.connect(fadeA); fadeA.connect(post);
  pre.connect(shaperB); shaperB.connect(fadeB); fadeB.connect(post);
  post.connect(master);
  master.connect(out);
  let liveIsA = true;

  // Filters of every SOUNDING voice, so a gestural cutoff/res sweep is audible
  // on the note that is playing. Before this, setCutoff/setRes only wrote to
  // P — read at the NEXT noteOn — which quantized the whole continuous-timbre
  // premise to note boundaries and made both Gate 8 arms unmeasurable.
  const live = new Set<{ f1: BiquadFilterNode; f2: BiquadFilterNode }>();

  function applyDrive(): void {
    const idle = liveIsA ? shaperB : shaperA;
    const fadeIn = liveIsA ? fadeB : fadeA;
    const fadeOut = liveIsA ? fadeA : fadeB;
    idle.curve = driveCurve(P.drive) as NonNullable<typeof idle.curve>;
    const now = ctx.currentTime;
    fadeIn.gain.setTargetAtTime(1, now, 0.015);
    fadeOut.gain.setTargetAtTime(0, now, 0.015);
    liveIsA = !liveIsA;
    pre.gain.setTargetAtTime(1 + P.drive * 2.2, now, 0.02);
    post.gain.setTargetAtTime(1 / (1 + P.drive * 1.1), now, 0.02);
  }
  applyDrive();

  function buildVoices(f1: BiquadFilterNode, baseF: number, when: number, stopAt: number) {
    const voxs: { o: OscillatorNode; ratio: number }[] = [];
    const u = Math.max(1, P.unison | 0);
    for (let i = 0; i < u; i++) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(baseF, when);
      o.detune.value = unisonFan(i, u) * P.detune;
      const g = ctx.createGain();
      g.gain.value = 0.55 / Math.sqrt(u);
      o.connect(g); g.connect(f1);
      o.start(when); o.stop(stopAt);
      voxs.push({ o, ratio: 1 });
    }
    if (P.sub > 0) {
      const o = ctx.createOscillator();
      const ratio = P.sub === 2 ? 0.25 : 0.5;
      o.type = P.sub === 2 ? 'sine' : 'square';
      o.frequency.setValueAtTime(baseF * ratio, when);
      const g = ctx.createGain();
      g.gain.value = P.sub === 2 ? 0.5 : 0.36;
      o.connect(g); g.connect(f1);
      o.start(when); o.stop(stopAt);
      voxs.push({ o, ratio });
    }
    return voxs;
  }

  function noteOn(midi: number, when: number, dur: number, vel = 1): SlideHandle {
    let midiNow = midi;
    const acc = Math.max(0, vel - 1);
    const gateEnd = when + Math.max(0.02, dur);
    const stopAt = gateEnd + P.r * 3 + 0.08;

    // the squelch — two poles leaning on each other
    const f1 = ctx.createBiquadFilter();
    const f2 = ctx.createBiquadFilter();
    f1.type = f2.type = 'lowpass';
    f1.Q.value = P.res; f2.Q.value = 0.55;
    const fc = clamp(40, 12000, P.cutoff);
    const peakF = Math.min(16000, fc * Math.pow(2, P.envAmt * 3.6 * (1 + acc * 0.55)));
    for (const f of [f1, f2]) {
      f.frequency.setValueAtTime(peakF, when);
      if (P.envAmt > 0) f.frequency.setTargetAtTime(fc, when + 0.002, Math.max(0.015, P.fdec / 3));
    }

    // the shape — ADSR with the accent lean
    const amp = ctx.createGain();
    const peak = 0.5 * vel;
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.linearRampToValueAtTime(peak, when + Math.max(0.0015, P.a));
    amp.gain.setTargetAtTime(Math.max(0.0001, peak * P.s), when + Math.max(0.0015, P.a), Math.max(0.012, P.d / 3));
    amp.gain.setTargetAtTime(0.0001, gateEnd, Math.max(0.015, P.r / 4));
    f1.connect(f2); f2.connect(amp); amp.connect(bus);

    const baseF = midiHz(midi);
    const voxs = buildVoices(f1, baseF, when, stopAt);
    if (P.penv) { // the dive
      const mul = Math.pow(2, P.penv / 12);
      for (const { o, ratio } of voxs) {
        o.frequency.setValueAtTime(baseF * ratio * mul, when);
        o.frequency.exponentialRampToValueAtTime(baseF * ratio, when + Math.max(0.02, P.ptime));
      }
    }
    const voiceRef = { f1, f2 };
    live.add(voiceRef);
    voxs[0].o.onended = () => {
      live.delete(voiceRef);
      try { amp.disconnect(); f1.disconnect(); f2.disconnect(); } catch { /* already torn down */ }
    };

    return {
      glideTo(m2, at, time) {
        const t = Math.max(0.005, time);
        for (const { o, ratio } of voxs) {
          o.frequency.cancelScheduledValues(at);
          o.frequency.setValueAtTime(midiHz(midiNow) * ratio, at);
          o.frequency.exponentialRampToValueAtTime(midiHz(m2) * ratio, at + t);
        }
        midiNow = m2;
      },
    };
  }

  return {
    noteOn,
    params: P,
    setCutoff(hz) {
      const fc = clamp(40, 12000, hz);
      // A still hand delivers the same value every frame; retargeting then
      // would supersede each note's own filter-envelope decay one frame after
      // its attack and flatten the squelch. Only a MOVING cutoff takes over
      // the sounding voices.
      if (Math.abs(fc - P.cutoff) < P.cutoff * 0.01) { P.cutoff = fc; return; }
      P.cutoff = fc;
      const now = ctx.currentTime;
      for (const v of live) {
        v.f1.frequency.setTargetAtTime(fc, now, 0.03);
        v.f2.frequency.setTargetAtTime(fc, now, 0.03);
      }
    },
    setRes(q) {
      const res = clamp(0.5, 24, q);
      if (Math.abs(res - P.res) < 0.05) { P.res = res; return; }
      P.res = res;
      const now = ctx.currentTime;
      for (const v of live) v.f1.Q.setTargetAtTime(res, now, 0.03);
    },
    setDrive(v) { P.drive = clamp(0, 1, v); applyDrive(); },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      live.clear();
      // Fade before disconnect: a bare disconnect while anything rings is an
      // instantaneous amplitude step — a click on every scene switch.
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02);
      setTimeout(() => { try { master.disconnect(); } catch { /* already gone */ } }, 120);
    },
  };
}
